import React, { useEffect } from 'react';
import { Routes, Route } from "react-router-dom";
import { MsalProvider, useMsal } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { PageLayout } from './components/PageLayout';
import { Home } from './pages/Home';
import { ProfileDetails } from './pages/ProfileDetails';
import { About } from './pages/About';
import AgencyInfo from './pages/AgencyInfo';
import Payments from './pages/Payments';
import DonorInfo from './pages/DonorInfo';
import DisplayEmail from './components/DisplayEmail';
import RequestAccessForm from './pages/RequestAccessForm';
import AgencySearch from './pages/AgencySearch';
import AdminPortal from './pages/AdminPortal';
import { b2cPolicies, loginRequest } from './authConfig';
import { compareIssuingPolicy } from './utils/claimUtils';
import useStore from './useStore';
import './styles/App.css';

const App = ({ instance }) => {
    return (
        <MsalProvider instance={instance}>
            <PageLayout>
                <AppRoutes />
            </PageLayout>
        </MsalProvider>
    );
};

const AppRoutes = () => {
    const { instance } = useMsal();
    const setUserEmail = useStore((state) => state.setUserEmail);

    useEffect(() => {
        const callbackId = instance.addEventCallback((event) => {
            if (
                (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
                event.payload.account
            ) {
                const emails = event.payload.account.idTokenClaims.emails;
                if (emails && emails.length > 0) {
                    const email = emails[0];
                    console.log('Email captured in AppRoutes:', email);
                    setUserEmail(email);
                } else {
                    console.error('No email found in idTokenClaims');
                }

                if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.editProfile)) {
                    const originalSignInAccount = instance
                        .getAllAccounts()
                        .find(
                            (account) =>
                                account.idTokenClaims.oid === event.payload.idTokenClaims.oid &&
                                account.idTokenClaims.sub === event.payload.idTokenClaims.sub && 
                                compareIssuingPolicy(account.idTokenClaims, b2cPolicies.names.signUpSignIn)
                        );

                    let signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        account: originalSignInAccount,
                    };

                    instance.ssoSilent(signUpSignInFlowRequest);
                }

                if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.resetPassword)) {
                    let signUpSignInFlowRequest = {
                        ...loginRequest,
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                    };
                    instance.loginRedirect(signUpSignInFlowRequest);
                }
            }

            if (event.eventType === EventType.LOGIN_FAILURE) {
                if (event.error && event.error.errorMessage.includes('AADB2C90118')) {
                    const resetPasswordRequest = {
                        authority: b2cPolicies.authorities.resetPassword.authority,
                        scopes: [],
                    };
                    instance.loginRedirect(resetPasswordRequest);
                }
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance, setUserEmail]);

    return (
        <Routes>
            <Route path="/about" element={<About />} />
            <Route path="/agency-info" element={<AgencyInfo />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/profile-details" element={<ProfileDetails />} />
            <Route path="/donor-info" element={<DonorInfo />} />
            <Route path="/display-email" element={<DisplayEmail />} />
            <Route path="/request-access" element={<RequestAccessForm />} />
            <Route path="/agency-search" element={<AgencySearch />} />
            <Route path="/admin-portal" element={<AdminPortal />} />
            <Route path="/" element={<Home />} />
        </Routes>
    );
};

export default App;
